import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AddCircle, Clear, CreateNewFolder, DeleteOutline, DriveFileMove, Edit, Folder, MoreVert, NoteAdd, Search, UnfoldLess, UnfoldMore } from '@mui/icons-material';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	InputAdornment,
	Menu,
	MenuItem,
	Paper,
	Stack,
	TextField,
	ToggleButton,
	Tooltip,
	Typography,
} from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';

import ConfirmDialog from '../../components/ConfirmDialog';
import { BoxResizeHandle } from './BoxResizeHandle';

export interface ArtifactItem {
	id: string;
	label: string;
	/** Child artifacts rendered beneath this artifact in the tree. */
	children?: ArtifactItem[];
	/** False for structural tree nodes, such as folders, that should not increase the artifact count. */
	countAsArtifact?: boolean;
	icon?: React.ReactNode;
	subtitle?: string;
	endAdornment?: React.ReactNode;
	/** Rendered immediately after the artifact name. */
	labelAdornment?: React.ReactNode;
	content?: React.ReactNode; // Custom content for the tree item label
	onDelete?: () => void | Promise<void>;
	onRename?: (name: string) => void | Promise<void>;
	onAddChild?: () => void | Promise<void>;
	folderId?: string | null;
	canMoveToFolder?: boolean;
	artifactType?: string;
	/** Value used by the optional category-specific subfilter. */
	subFilterValue?: string;
}

export interface ArtifactFolder {
	id: string;
	categoryId?: string;
	name: string;
	parentFolderId?: string | null;
	order?: number;
	/** Optional count override when folder contents are not rendered as tree items. */
	itemCount?: number;
}

export interface ArtifactCategory {
	id: string;
	parentId?: string;
	label: string;
	artifactType?: string;
	folderArtifactType?: string;
	icon: React.ReactNode;
	endAdornment?: React.ReactNode;
	items: ArtifactItem[];
	/** Optional count override for artifacts omitted from the tree. */
	itemCount?: number;
	emptyLabel?: string;
	onAddItem?: () => void;
	addItemLabel?: string;
	folders?: ArtifactFolder[];
	onCreateFolder?: (name: string, parentFolderId?: string) => void | Promise<void>;
	onRenameFolder?: (folderId: string, name: string, parentFolderId?: string) => void | Promise<void>;
	onDeleteFolder?: (folderId: string) => void | Promise<void>;
	onMoveItemToFolder?: (itemId: string, folderId: string | null) => void | Promise<void>;
}

export interface ArtifactPanelProps {
	title: string;
	titleIcon?: React.ReactNode;
	categories: ArtifactCategory[];
	staticItems?: ArtifactItem[]; // Items that aren't in a category (like Theme)
	selectedItemId: string | null;
	onSelectItem: (itemId: string | null) => void;
	searchPlaceholder?: string;
	searchActions?: React.ReactNode;
	searchFilter?: (item: ArtifactItem, searchTerm: string) => boolean;
	selectFolders?: boolean;
	showArtifactTypeFilters?: boolean;
	subFilter?: { categoryId: string; label: string; options: { value: string; label: string }[] };
	width?: number | string;
	defaultWidth?: number;
	minWidth?: number;
	maxWidth?: number;
	storageKey?: string;
	expandAllLabel?: string;
	collapseAllLabel?: string;
}

const DEFAULT_WIDTH = 300;
const MIN_WIDTH = 200;
const MAX_WIDTH = 800;
const headerActionSx = {
	color: 'text.secondary',
	borderRadius: 0,
	'&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
};

/**
 * A reusable panel component for displaying artifacts in a tree structure with categories.
 * Supports resizing, searching, expanding/collapsing, and custom item rendering.
 */
export function ArtifactPanel({
	title,
	titleIcon,
	categories,
	staticItems = [],
	selectedItemId,
	onSelectItem,
	searchPlaceholder = 'Search…',
	searchActions,
	searchFilter,
	selectFolders = false,
	showArtifactTypeFilters = false,
	subFilter,
	width: controlledWidth,
	defaultWidth = DEFAULT_WIDTH,
	minWidth = MIN_WIDTH,
	maxWidth = MAX_WIDTH,
	storageKey,
	expandAllLabel = 'Expand all',
	collapseAllLabel = 'Collapse all',
}: ArtifactPanelProps) {
	const [panelWidth, setPanelWidth] = useState<number>(defaultWidth);
	const panelRef = useRef<HTMLDivElement>(null);

	const [searchTerm, setSearchTerm] = useState('');
	const [selectedArtifactTypes, setSelectedArtifactTypes] = useState<string[] | null>(null);
	const [selectedSubFilterValue, setSelectedSubFilterValue] = useState<string | null>(null);
	const [expandedItems, setExpandedItems] = useState<string[]>(categories.map((c) => c.id));
	const expandedItemsBeforeSearchRef = useRef<string[] | null>(null);
	const previousSearchTermRef = useRef('');
	const [folderDialog, setFolderDialog] = useState<{ categoryId: string; folderId?: string; parentFolderId?: string; name: string } | null>(null);
	const [itemActionMenu, setItemActionMenu] = useState<{ anchorEl: HTMLElement; category: ArtifactCategory; item: ArtifactItem } | null>(null);
	const [folderActionMenu, setFolderActionMenu] = useState<{ anchorEl: HTMLElement; category: ArtifactCategory; folder: ArtifactFolder } | null>(null);
	const [deleteConfirmItem, setDeleteConfirmItem] = useState<ArtifactItem | null>(null);
	const [renameItemDialog, setRenameItemDialog] = useState<{ item: ArtifactItem; name: string } | null>(null);
	const [deleteConfirmFolder, setDeleteConfirmFolder] = useState<{ category: ArtifactCategory; folder: ArtifactFolder } | null>(null);

	const width = controlledWidth ?? panelWidth;
	const folderDialogFolders = categories.find((category) => category.id === folderDialog?.categoryId)?.folders ?? [];
	const folderDialogFoldersById = new Map(folderDialogFolders.map((folder) => [folder.id, folder]));
	const availableParentFolders = folderDialogFolders.filter((candidate) => {
		if (candidate.id === folderDialog?.folderId) return false;
		let parentId = candidate.parentFolderId;
		const visited = new Set<string>();
		while (parentId && !visited.has(parentId)) {
			if (parentId === folderDialog?.folderId) return false;
			visited.add(parentId);
			parentId = folderDialogFoldersById.get(parentId)?.parentFolderId ?? undefined;
		}
		return true;
	});

	// Default search filter: search by label
	const defaultSearchFilter = useCallback((item: ArtifactItem, search: string) => {
		const lowerSearch = search.trim().toLowerCase();
		return item.label.toLowerCase().includes(lowerSearch) || (item.subtitle?.toLowerCase().includes(lowerSearch) ?? false);
	}, []);

	const filterFunc = searchFilter ?? defaultSearchFilter;
	const artifactTypeOptions = useMemo(() => {
		const types = new Set<string>();
		const addItemTypes = (items: ArtifactItem[]) => {
			for (const item of items) {
				if (item.artifactType) {
					types.add(item.artifactType);
				}
				addItemTypes(item.children ?? []);
			}
		};
		for (const category of categories) {
			if (category.artifactType) {
				types.add(category.artifactType);
			}
			if (category.folderArtifactType) {
				types.add(category.folderArtifactType);
			}
			addItemTypes(category.items);
		}
		return Array.from(types).sort((left, right) => left.localeCompare(right));
	}, [categories]);

	const typeFilteredCategories = useMemo(() => {
		const selectedTypes = selectedArtifactTypes === null ? null : new Set(selectedArtifactTypes);
		const filterItems = (items: ArtifactItem[]): ArtifactItem[] =>
			items.flatMap((item) => {
				if (item.artifactType && selectedTypes && !selectedTypes.has(item.artifactType)) {
					return [];
				}
				const children = item.children ? filterItems(item.children) : undefined;
				if (item.children?.length && !children?.length) {
					return [];
				}
				return [{ ...item, children }];
			});

		const filterBySubFilter = (items: ArtifactItem[]): ArtifactItem[] =>
			items.flatMap((item) => {
				const children = item.children ? filterBySubFilter(item.children) : undefined;
				const itemMatches = item.subFilterValue === selectedSubFilterValue;
				// Keep a parent in the tree when it contains a matching descendant.
				if (itemMatches || (children?.length ?? 0) > 0) {
					return [{ ...item, children }];
				}
				return [];
			});

		return categories.flatMap((category) => {
			if (category.artifactType && selectedTypes && !selectedTypes.has(category.artifactType)) {
				return [];
			}
			let items = filterItems(category.items);
			if (subFilter?.categoryId === category.id && selectedSubFilterValue !== null) {
				items = filterBySubFilter(items);
			}
			return [
				{
					...category,
					items,
					folders: category.folderArtifactType && selectedTypes && !selectedTypes.has(category.folderArtifactType) ? [] : category.folders,
				},
			];
		});
	}, [categories, selectedArtifactTypes, selectedSubFilterValue, subFilter]);

	// Filter items in each category based on search term
	const filteredCategories = useMemo(() => {
		if (!searchTerm.trim()) return typeFilteredCategories;

		const normalizedSearch = searchTerm.trim().toLowerCase();
		const filterItem = (item: ArtifactItem): ArtifactItem | null => {
			const children = item.children?.map(filterItem).filter((child): child is ArtifactItem => child !== null);
			if (filterFunc(item, normalizedSearch)) {
				return { ...item, children: item.children };
			}
			return children?.length ? { ...item, children } : null;
		};
		// Search narrows the currently selected artifact and subtype filters (AND semantics).
		const searchCategories = typeFilteredCategories;
		const categoriesById = new Map(searchCategories.map((category) => [category.id, category]));
		const visibleIds = new Set<string>();
		const addAncestors = (category: ArtifactCategory) => {
			let current: ArtifactCategory | undefined = category;
			const visited = new Set<string>();
			while (current && !visited.has(current.id)) {
				visited.add(current.id);
				visibleIds.add(current.id);
				current = current.parentId ? categoriesById.get(current.parentId) : undefined;
			}
		};
		const addDescendants = (category: ArtifactCategory) => {
			visibleIds.add(category.id);
			for (const child of searchCategories.filter((candidate) => candidate.parentId === category.id)) {
				addDescendants(child);
			}
		};

		const filteredById = new Map<string, ArtifactCategory>();
		for (const category of searchCategories) {
			const categoryMatches = category.label.toLowerCase().includes(normalizedSearch);
			const filteredCategory = {
				...category,
				items: categoryMatches ? category.items : category.items.map(filterItem).filter((item): item is ArtifactItem => item !== null),
			};
			filteredById.set(category.id, filteredCategory);
			if (categoryMatches) {
				addDescendants(category);
				addAncestors(category);
			} else if (filteredCategory.items.length > 0) {
				addAncestors(category);
			}
		}

		return searchCategories.flatMap((category) => (visibleIds.has(category.id) ? [filteredById.get(category.id)!] : []));
	}, [typeFilteredCategories, searchTerm, filterFunc]);

	const normalizedSearchTerm = searchTerm.trim();
	const searchExpandedItemsKey = normalizedSearchTerm
		? filteredCategories
				.flatMap((category) => {
					const expandableItemIds = (items: ArtifactItem[]): string[] =>
						items.flatMap((item) => [...(item.children?.length ? [item.id] : []), ...expandableItemIds(item.children ?? [])]);
					return [category.id, ...expandableItemIds(category.items)];
				})
				.join('|')
		: '';
	useEffect(() => {
		const wasSearching = Boolean(previousSearchTermRef.current);
		const isSearching = Boolean(normalizedSearchTerm);
		if (isSearching) {
			if (!wasSearching) {
				expandedItemsBeforeSearchRef.current = expandedItems;
			}
			setExpandedItems(searchExpandedItemsKey ? searchExpandedItemsKey.split('|') : []);
		} else if (wasSearching && expandedItemsBeforeSearchRef.current) {
			setExpandedItems(expandedItemsBeforeSearchRef.current);
			expandedItemsBeforeSearchRef.current = null;
		}
		previousSearchTermRef.current = normalizedSearchTerm;
		// Expansion is intentionally captured only when a search begins.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [normalizedSearchTerm, searchExpandedItemsKey]);

	const filteredStaticItems = useMemo(() => {
		if (!searchTerm) return staticItems;
		return staticItems.filter((item) => filterFunc(item, searchTerm));
	}, [staticItems, searchTerm, filterFunc]);

	const handleSelectedItemsChange = useCallback(
		(_event: React.SyntheticEvent, itemId: string | null) => {
			const isStructuralItem = (items: ArtifactItem[]): boolean =>
				items.some((item) => (item.id === itemId && item.countAsArtifact === false) || isStructuralItem(item.children ?? []));
			const isFolder = categories.some((category) => category.folders?.some((folder) => folder.id === itemId));
			if (
				itemId &&
				(categories.some((category) => category.id === itemId || isStructuralItem(category.items)) ||
					(!selectFolders && isFolder) ||
					itemId.endsWith(':empty'))
			) {
				return;
			}
			onSelectItem(itemId);
		},
		[categories, onSelectItem, selectFolders],
	);

	const handleExpandAll = useCallback(() => {
		const getExpandableStructuralItems = (items: ArtifactItem[]): string[] =>
			items.flatMap((item) => [
				...(item.countAsArtifact === false && item.children?.length ? [item.id] : []),
				...getExpandableStructuralItems(item.children ?? []),
			]);
		setExpandedItems([...categories.map((category) => category.id), ...categories.flatMap((category) => getExpandableStructuralItems(category.items))]);
	}, [categories]);

	const handleCollapseAll = useCallback(() => {
		setExpandedItems([]);
	}, []);

	const handleSaveFolder = useCallback(async () => {
		if (!folderDialog) return;

		const category = categories.find((candidate) => candidate.id === folderDialog.categoryId);
		const name = folderDialog.name.trim();
		if (!category || !name) return;

		if (folderDialog.folderId) {
			await category.onRenameFolder?.(folderDialog.folderId, name, folderDialog.parentFolderId);
		} else {
			await category.onCreateFolder?.(name, folderDialog.parentFolderId);
		}
		setFolderDialog(null);
	}, [categories, folderDialog]);

	const handleConfirmDeleteArtifact = useCallback(async () => {
		const item = deleteConfirmItem;
		setDeleteConfirmItem(null);
		await item?.onDelete?.();
	}, [deleteConfirmItem]);

	const handleConfirmDeleteFolder = useCallback(async () => {
		const folder = deleteConfirmFolder;
		setDeleteConfirmFolder(null);
		await folder?.category.onDeleteFolder?.(folder.folder.id);
	}, [deleteConfirmFolder]);

	const categoryItemCounts = useMemo(() => {
		const categoriesById = new Map(filteredCategories.map((category) => [category.id, category]));
		const childrenByParent = new Map<string, ArtifactCategory[]>();
		for (const category of filteredCategories) {
			if (category.parentId) {
				childrenByParent.set(category.parentId, [...(childrenByParent.get(category.parentId) ?? []), category]);
			}
		}
		const counts = new Map<string, number>();
		const countItems = (categoryId: string, visiting = new Set<string>()): number => {
			if (visiting.has(categoryId)) return 0;
			const category = categoriesById.get(categoryId);
			if (!category) return 0;
			visiting.add(categoryId);
			const countArtifacts = (items: ArtifactItem[]): number =>
				items.reduce((total, item) => total + (item.countAsArtifact === false ? 0 : 1) + countArtifacts(item.children ?? []), 0);
			const count = (category.itemCount ?? countArtifacts(category.items)) + (childrenByParent.get(categoryId) ?? []).reduce((total, child) => total + countItems(child.id, visiting), 0);
			visiting.delete(categoryId);
			counts.set(categoryId, count);
			return count;
		};
		filteredCategories.forEach((category) => countItems(category.id));
		return counts;
	}, [filteredCategories]);

	const renderCategoryLabel = (category: ArtifactCategory) => (
		<Stack direction="row" alignItems="center" spacing={1}>
			{category.icon}
			<Typography variant="body2" sx={{ flex: 1 }}>
				{category.label} ({categoryItemCounts.get(category.id) ?? 0})
			</Typography>
			{category.endAdornment}
			{category.onAddItem && (
				<IconButton
					size="small"
					onClick={(event) => {
						event.stopPropagation();
						category.onAddItem?.();
					}}
					aria-label={category.addItemLabel ?? `Add ${category.label}`}
				>
					<AddCircle fontSize="small" />
				</IconButton>
			)}
			{category.onCreateFolder && (
				<Tooltip title="Create folder">
					<IconButton
						size="small"
						onClick={(event) => {
							event.stopPropagation();
							setFolderDialog({ categoryId: category.id, name: '' });
						}}
						aria-label={`Create ${category.label} folder`}
					>
						<CreateNewFolder fontSize="small" />
					</IconButton>
				</Tooltip>
			)}
		</Stack>
	);

	const renderCategoryContents = (category: ArtifactCategory) => {
		const folders = [...(category.folders ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.name.localeCompare(b.name));
		const folderIds = new Set(folders.map((folder) => folder.id));
		const rootItems = category.items.filter((item) => !item.folderId || !folderIds.has(item.folderId));
		const childCategories = filteredCategories.filter((candidate) => candidate.parentId === category.id);

		return (
			<>
				{childCategories.map((child) => (
					<TreeItem key={child.id} itemId={child.id} label={renderCategoryLabel(child)}>
						{renderCategoryContents(child)}
					</TreeItem>
				))}
				{rootItems.map((item) => renderArtifactItem(item, category))}
				{folders.filter((folder) => !folder.parentFolderId || !folderIds.has(folder.parentFolderId)).map((folder) => renderFolder(folder, category, folders))}
				{childCategories.length === 0 && rootItems.length === 0 && folders.length === 0 && category.emptyLabel && (
					<TreeItem
						itemId={`${category.id}:empty`}
						label={
							<Typography variant="body2" color="textSecondary" sx={{ px: 0.5 }}>
								{category.emptyLabel}
							</Typography>
						}
					/>
				)}
			</>
		);
	};

	const renderFolder = (folder: ArtifactFolder, category: ArtifactCategory, folders: ArtifactFolder[]): React.ReactNode => {
		const folderItems = category.items.filter((item) => item.folderId === folder.id);
		const itemCount = folder.itemCount ?? folderItems.length;
		const childFolders = folders.filter((candidate) => candidate.parentFolderId === folder.id);
		const hasFolderActions = Boolean(category.onCreateFolder || category.onRenameFolder || category.onDeleteFolder);
		const matchesSearch = !searchTerm || folder.name.toLowerCase().includes(searchTerm.trim().toLowerCase());
		const hasMatchingDescendant = (candidate: ArtifactFolder): boolean =>
			category.items.some((item) => item.folderId === candidate.id && filterFunc(item, searchTerm.trim().toLowerCase())) ||
			folders.some((child) => child.parentFolderId === candidate.id && (child.name.toLowerCase().includes(searchTerm.trim().toLowerCase()) || hasMatchingDescendant(child)));
		if (searchTerm && !matchesSearch && !hasMatchingDescendant(folder)) {
			return null;
		}

		return (
			<TreeItem
				key={`${category.id}:folder:${folder.id}`}
				itemId={folder.id}
				label={
					<Stack direction="row" alignItems="center" spacing={0.5}>
						<Folder fontSize="small" />
						<Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
							{folder.name} ({itemCount})
						</Typography>
						{hasFolderActions && (
							<IconButton
								size="small"
								onClick={(event) => {
									event.stopPropagation();
									setFolderActionMenu({ anchorEl: event.currentTarget, category, folder });
								}}
								aria-label={`${folder.name} actions`}
							>
								<MoreVert fontSize="small" />
							</IconButton>
						)}
					</Stack>
				}
			>
				{childFolders.map((child) => renderFolder(child, category, folders))}
				{folderItems.map((item) => renderArtifactItem(item, category))}
				{itemCount === 0 && childFolders.length === 0 && (
					<TreeItem itemId={`${category.id}:folder:${folder.id}:empty`} label={<Typography variant="body2" color="textSecondary" sx={{ px: 0.5 }}>Empty folder</Typography>} />
				)}
			</TreeItem>
		);
	};

	const renderItemLabel = useCallback((item: ArtifactItem, category?: ArtifactCategory) => {
		const hasMoveAction = Boolean(category?.onMoveItemToFolder && item.canMoveToFolder !== false);
		const hasItemActions = hasMoveAction || Boolean(item.onDelete || item.onRename || item.onAddChild);

		return (
			item.content ?? (
				<Stack direction="row" alignItems="center" spacing={0.5}>
					{item.icon}
					<Stack sx={{ flex: 1, minWidth: 0 }}>
						<Stack direction="row" alignItems="center" spacing={0.5}>
							<Typography variant="body2" noWrap>{item.label}</Typography>
							{item.labelAdornment}
						</Stack>
						{item.subtitle && (
							<Typography variant="caption" color="textSecondary">
								{item.subtitle}
							</Typography>
						)}
					</Stack>
					{item.endAdornment}
					{hasItemActions && category && (
						<IconButton
							size="small"
							onClick={(e) => {
								e.stopPropagation();
								setItemActionMenu({ anchorEl: e.currentTarget, category, item });
							}}
							aria-label={`${item.label} actions`}
						>
							<MoreVert fontSize="small" />
						</IconButton>
					)}
				</Stack>
			)
		);
	}, []);

	function renderArtifactItem(item: ArtifactItem, category: ArtifactCategory) {
		return (
			<TreeItem key={item.id} itemId={item.id} label={renderItemLabel(item, category)}>
				{item.children?.map((child) => renderArtifactItem(child, category))}
			</TreeItem>
		);
	}

	return (
		<Paper
			ref={panelRef}
			sx={{
				position: 'relative',
				width: typeof width === 'number' ? `${width}px` : width,
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
				bgcolor: 'background.paper',
				minHeight: 0,
				flexShrink: 0,
				overflow: 'hidden',
			}}
		>
			{!controlledWidth && storageKey && (
				<BoxResizeHandle
					position="right"
					width={panelWidth}
					setWidth={setPanelWidth}
					panelRef={panelRef}
					storageKey={storageKey}
					defaultWidth={defaultWidth}
					minWidth={minWidth}
					maxWidth={maxWidth}
					direction="left"
				/>
			)}

			<Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
				{/* Header — mirrors the chat panel title and grouped actions. */}
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						px: 1.5,
						py: 1.25,
						flexShrink: 0,
						bgcolor: 'background.default',
						borderBottom: 1,
						borderColor: 'divider',
					}}
				>
					{titleIcon && <Box sx={{ display: 'flex', alignItems: 'center', mr: 1, flexShrink: 0 }}>{titleIcon}</Box>}
					<Typography variant="body2" noWrap sx={{ color: 'text.primary', fontWeight: 700, minWidth: 0, flex: 1 }}>
						{title}
					</Typography>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							border: '1px solid',
							borderColor: 'divider',
							borderRadius: 1.5,
							overflow: 'hidden',
							'& > :not(:last-child)': { borderRight: '1px solid', borderColor: 'divider' },
						}}
					>
						<Tooltip title={expandAllLabel}>
							<span>
								<IconButton size="small" onClick={handleExpandAll} aria-label={expandAllLabel} sx={headerActionSx}>
									<UnfoldMore fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
						<Tooltip title={collapseAllLabel}>
							<span>
								<IconButton size="small" onClick={handleCollapseAll} aria-label={collapseAllLabel} sx={headerActionSx}>
									<UnfoldLess fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
					</Box>
				</Box>

				{/* Search */}
				<Box sx={{ px: 1, py: 0.75, flexShrink: 0 }}>
					<Stack direction="row" spacing={0.75} alignItems="center">
						<TextField
							size="small"
							fullWidth
							placeholder={searchPlaceholder}
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							slotProps={{
								input: {
									startAdornment: (
										<InputAdornment position="start">
											<Search fontSize="small" />
										</InputAdornment>
									),
									endAdornment: searchTerm ? (
										<InputAdornment position="end">
											<IconButton size="small" onClick={() => setSearchTerm('')} aria-label="Clear search">
												<Clear fontSize="small" />
											</IconButton>
										</InputAdornment>
									) : undefined,
								},
							}}
						/>
						{searchActions}
					</Stack>
				</Box>
				{showArtifactTypeFilters && artifactTypeOptions.length > 0 && (
					<Box sx={{ px: 1, pb: 0.75, flexShrink: 0 }}>
						<Box role="group" aria-label="Filter artifacts by type" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
							<ToggleButton
								value="all"
								selected={selectedArtifactTypes === null}
								size="small"
								onClick={() => setSelectedArtifactTypes(null)}
								sx={{ px: 1, py: 0.25, minHeight: 26, fontSize: '0.7rem', textTransform: 'none' }}
							>
								All
							</ToggleButton>
							{artifactTypeOptions.map((artifactType) => {
								const selected = selectedArtifactTypes?.includes(artifactType) ?? false;
								return (
									<ToggleButton
										key={artifactType}
										value={artifactType}
										selected={selected}
										size="small"
										aria-label={`Show ${artifactType} artifacts`}
										onClick={() =>
											setSelectedArtifactTypes((current) => {
												const next = current ?? [];
												const updated = selected ? next.filter((type) => type !== artifactType) : [...next, artifactType];
												return updated.length && updated.length < artifactTypeOptions.length ? updated : null;
											})
										}
										sx={{ px: 1, py: 0.25, minHeight: 26, fontSize: '0.7rem', textTransform: 'none' }}
									>
										{artifactType}
									</ToggleButton>
								);
							})}
						</Box>
					</Box>
				)}
				{subFilter && subFilter.options.length > 0 && (
					<Box sx={{ px: 1, pb: 0.75, flexShrink: 0 }}>
						<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
							{subFilter.label}
						</Typography>
						<Box role="group" aria-label={subFilter.label} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
							<ToggleButton
								value="all"
								selected={selectedSubFilterValue === null}
								size="small"
								onClick={() => setSelectedSubFilterValue(null)}
								sx={{ px: 1, py: 0.25, minHeight: 26, fontSize: '0.7rem', textTransform: 'none' }}
							>
								All
							</ToggleButton>
							{subFilter.options.map((option) => (
								<ToggleButton
									key={option.value}
									value={option.value}
									selected={selectedSubFilterValue === option.value}
									size="small"
									aria-label={`Show ${option.label} ${subFilter.label.toLowerCase()}`}
									onClick={() => setSelectedSubFilterValue((current) => (current === option.value ? null : option.value))}
									sx={{ px: 1, py: 0.25, minHeight: 26, fontSize: '0.7rem', textTransform: 'none' }}
								>
									{option.label}
								</ToggleButton>
							))}
						</Box>
					</Box>
				)}
				<Divider />

				{/* Tree View */}
				<Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
					<SimpleTreeView
						selectedItems={selectedItemId}
						onSelectedItemsChange={handleSelectedItemsChange}
						expandedItems={expandedItems}
						onExpandedItemsChange={(_e, ids) => setExpandedItems(ids)}
						sx={{
							p: 1,
							'& .MuiTreeItem-groupTransition': { transitionDuration: '180ms !important' },
							'@media (prefers-reduced-motion: reduce)': {
								'& .MuiTreeItem-groupTransition': { transitionDuration: '0.01ms !important' },
							},
						}}
					>
						{/* Static Items */}
						{filteredStaticItems.map((item) => (
							<TreeItem key={item.id} itemId={item.id} label={renderItemLabel(item)} />
						))}

						{/* Categories */}
						{filteredCategories
							.filter((category) => !category.parentId)
							.map((category) => (
								<TreeItem key={category.id} itemId={category.id} label={renderCategoryLabel(category)}>
									{renderCategoryContents(category)}
								</TreeItem>
							))}
					</SimpleTreeView>
				</Box>
			</Box>
			<Menu
				anchorEl={itemActionMenu?.anchorEl ?? null}
				open={Boolean(itemActionMenu)}
				onClose={() => setItemActionMenu(null)}
				onClick={(e) => e.stopPropagation()}
			>
				{itemActionMenu?.category.onMoveItemToFolder &&
					itemActionMenu.item.canMoveToFolder !== false && [
						<MenuItem
							key="remove-from-folder"
							onClick={async () => {
								await itemActionMenu.category.onMoveItemToFolder?.(itemActionMenu.item.id, null);
								setItemActionMenu(null);
							}}
						>
							<DriveFileMove fontSize="small" sx={{ mr: 1 }} />
							Remove from folder
						</MenuItem>,
						...(itemActionMenu.category.folders ?? []).map((folder) => (
							<MenuItem
								key={folder.id}
								onClick={async () => {
									await itemActionMenu.category.onMoveItemToFolder?.(itemActionMenu.item.id, folder.id);
									setItemActionMenu(null);
								}}
							>
								<Folder fontSize="small" sx={{ mr: 1 }} />
								Move to {folder.name}
							</MenuItem>
						)),
					]}
				{itemActionMenu?.item.onDelete && (
					<MenuItem
						onClick={() => {
							setDeleteConfirmItem(itemActionMenu.item);
							setItemActionMenu(null);
						}}
					>
						<DeleteOutline fontSize="small" sx={{ mr: 1 }} />
						Delete
					</MenuItem>
				)}
				{itemActionMenu?.item.onRename && (
					<MenuItem
						onClick={() => {
							setRenameItemDialog({ item: itemActionMenu.item, name: itemActionMenu.item.label });
							setItemActionMenu(null);
						}}
					>
						<Edit fontSize="small" sx={{ mr: 1 }} />
						Rename
					</MenuItem>
				)}
				{itemActionMenu?.item.onAddChild && (
					<MenuItem
						onClick={async () => {
							await itemActionMenu.item.onAddChild?.();
							setItemActionMenu(null);
						}}
					>
						<NoteAdd fontSize="small" sx={{ mr: 1 }} />
						New file
					</MenuItem>
				)}
			</Menu>
			<Menu
				anchorEl={folderActionMenu?.anchorEl ?? null}
				open={Boolean(folderActionMenu)}
				onClose={() => setFolderActionMenu(null)}
				onClick={(e) => e.stopPropagation()}
			>
				{folderActionMenu?.category.onRenameFolder && (
					<MenuItem
						onClick={() => {
							setFolderDialog({
								categoryId: folderActionMenu.category.id,
								folderId: folderActionMenu.folder.id,
								parentFolderId: folderActionMenu.folder.parentFolderId ?? undefined,
								name: folderActionMenu.folder.name,
							});
							setFolderActionMenu(null);
						}}
					>
						<Edit fontSize="small" sx={{ mr: 1 }} />
						Rename
					</MenuItem>
				)}
				{folderActionMenu?.category.onCreateFolder && (
					<MenuItem
						onClick={() => {
							setFolderDialog({ categoryId: folderActionMenu.category.id, parentFolderId: folderActionMenu.folder.id, name: '' });
							setFolderActionMenu(null);
						}}
					>
						<CreateNewFolder fontSize="small" sx={{ mr: 1 }} />
						Create subfolder
					</MenuItem>
				)}
				{folderActionMenu?.category.onDeleteFolder && (
					<MenuItem
						onClick={() => {
							setDeleteConfirmFolder({ category: folderActionMenu.category, folder: folderActionMenu.folder });
							setFolderActionMenu(null);
						}}
					>
						<DeleteOutline fontSize="small" sx={{ mr: 1 }} />
						Delete
					</MenuItem>
				)}
			</Menu>
			<Dialog open={Boolean(folderDialog)} onClose={() => setFolderDialog(null)} maxWidth="xs" fullWidth>
				<DialogTitle>{folderDialog?.folderId ? 'Rename folder' : 'Create folder'}</DialogTitle>
				<DialogContent>
					<TextField
						autoFocus
						fullWidth
						margin="dense"
						label="Folder name"
						value={folderDialog?.name ?? ''}
						onChange={(e) => setFolderDialog((current) => (current ? { ...current, name: e.target.value } : current))}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								handleSaveFolder();
							}
						}}
					/>
					<TextField
						select
						fullWidth
						margin="dense"
						label="Parent folder"
						value={folderDialog?.parentFolderId ?? ''}
						onChange={(event) => setFolderDialog((current) => current ? { ...current, parentFolderId: event.target.value || undefined } : current)}
					>
						<MenuItem value="">Top level</MenuItem>
						{availableParentFolders.map((candidate) => <MenuItem key={candidate.id} value={candidate.id}>{candidate.name}</MenuItem>)}
					</TextField>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setFolderDialog(null)}>Cancel</Button>
					<Button variant="contained" onClick={handleSaveFolder} disabled={!folderDialog?.name.trim()}>
						Save
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog open={Boolean(renameItemDialog)} onClose={() => setRenameItemDialog(null)} maxWidth="xs" fullWidth>
				<DialogTitle>Rename item</DialogTitle>
				<DialogContent>
					<TextField
						autoFocus
						fullWidth
						margin="dense"
						label="Name"
						value={renameItemDialog?.name ?? ''}
						onChange={(event) => setRenameItemDialog((current) => current ? { ...current, name: event.target.value } : current)}
						onKeyDown={(event) => {
							if (event.key === 'Enter' && renameItemDialog?.name.trim()) {
								void renameItemDialog.item.onRename?.(renameItemDialog.name.trim());
								setRenameItemDialog(null);
							}
						}}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setRenameItemDialog(null)}>Cancel</Button>
					<Button
						variant="contained"
						disabled={!renameItemDialog?.name.trim()}
						onClick={() => {
							if (renameItemDialog) void renameItemDialog.item.onRename?.(renameItemDialog.name.trim());
							setRenameItemDialog(null);
						}}
					>
						Rename
					</Button>
				</DialogActions>
			</Dialog>
			<ConfirmDialog
				open={Boolean(deleteConfirmItem)}
				title="Delete Artifact"
				message={`Are you sure you want to delete "${deleteConfirmItem?.label ?? 'this artifact'}"? This action cannot be undone.`}
				confirmText="Delete"
				confirmColor="error"
				onConfirm={handleConfirmDeleteArtifact}
				onCancel={() => setDeleteConfirmItem(null)}
			/>
			<ConfirmDialog
				open={Boolean(deleteConfirmFolder)}
				title="Delete Folder"
				message={`Are you sure you want to delete the folder "${deleteConfirmFolder?.folder.name ?? 'this folder'}"? This action cannot be undone. Non-empty folders may be rejected.`}
				confirmText="Delete"
				confirmColor="error"
				onConfirm={handleConfirmDeleteFolder}
				onCancel={() => setDeleteConfirmFolder(null)}
			/>
		</Paper>
	);
}
